import React, {
	KeyboardEvent,
	ReactElement,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import classnames from "classnames";
import {debounce} from "lodash";
import {Breathing} from "react-shimmer";

import {count} from "../../utils/text";
import {EmptyComponent} from "../../types";
import {useToastApi} from "../../api";
import {inflateImages, inflateVideos} from "../../data";
import {Card} from "../card";
import {useToast} from "../../toast";
import {Button, Checkbox, InputRow, MultiColumn, Option, Select, Text, required} from "../input";
import {Span} from "../text";
import {Icon} from "../images";
import {useConfirmModal, useModal} from "../../modals";
import {S3UploadComponent} from "./s3-video-upload";
import {ImageLoader} from "./image-loader";
import {ImageEditor} from ".";
import {FileUpload} from "../input/file-upload";
import {inflateMediaUsers, inflateMetadata, inflateUploadedImage, Media} from "../../data/media";
import {Loading} from "../loading";

import styles from "./media-selector.module.scss";

interface MediaSelectorProps extends EmptyComponent {
	type: "image" | "video";
	selectedMedia: Media[];
	onChangeSelectedMedia: (media: Media[]) => void;
	onConfirm: () => void;
}

interface MediaFilter {
	page: number;
	totalPages: number;
}

export const MediaSelector = ({
	className,
	selectedMedia,
	onChangeSelectedMedia,
	type,
	onConfirm,
}: MediaSelectorProps): ReactElement => {
	const {get, put, post, del} = useToastApi();
	const defaultFilter = useMemo<MediaFilter>(
		() => ({
			totalPages: 1,
			page: 0,
		}),
		[]
	);
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [imageSize, setImageSize] = useState({width: 0, height: 0});
	const [isDetailView, setIsDetailView] = useState(false);
	const [filter, setFilter] = useState<MediaFilter>({
		totalPages: 1,
		page: 0,
	});
	const [media, setMedia] = useState<Media[]>([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const toast = useToast();
	const [users, setUsers] = useState<Option<number>[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);
	const [file, setFile] = useState<File>();
	const [createdBy, setCreatedBy] = useState<number>();
	const [search, setSearch] = useState("");
	const imageUploadedModal = useConfirmModal(
		() => ({
			title: "File uploaded to Media Library.",
			body: "You can now use your new file for your posts.",
			onConfirm: close => close(),
		}),
		[]
	);

	const fetchData = useCallback(
		(filter: MediaFilter) => {
			if (filter.page + 1 > filter.totalPages) {
				return;
			}

			setLoading(true);
			get(
				`${type} list`,
				`/${type}s/${type}-list/${filter.page + 1}?count=15${search ? `&title=${search}` : ""}${
					createdBy ? `&created_by=${createdBy}` : ""
				}`,
				data => {
					setLoading(false);
					const metaData = data ? inflateMetadata(data) : {totalPages: 0, count: 0, page: 1};
					const newMedia = type === "image" ? inflateImages(data ?? []) : inflateVideos(data ?? []);
					setFilter(metaData);
					setMedia(media => [
						...media,
						...newMedia.map(m => ({
							id: m.id,
							url: m.url,
							alt: m.title,
							title: m.title,
							isDefault: "isDefault" in m ? m.isDefault : false,
							type,
							...(type === "video" && {thumbnailUrl: m.thumbnailUrl}),
						})),
					]);
				}
			);
		},
		[createdBy, search, type, get]
	);

	const resetFetchData = useCallback(() => {
		setMedia([]);
		onChangeSelectedMedia([]);
		fetchData(defaultFilter);
	}, [defaultFilter, fetchData, onChangeSelectedMedia]);

	const handleDelete = useCallback(
		close => {
			const onComplete = () => {
				resetFetchData();
				close();
			};

			if (type === "image") {
				return post(
					"image delete",
					`/images/multi-delete`,
					selectedMedia.map(m => m.id),
					onComplete,
					undefined,
					{}
				);
			}

			del("video delete", `/videos/video-list`, {orgVideoIds: selectedMedia.map(m => m.id)}, onComplete);
		},
		[del, post, resetFetchData, selectedMedia, type]
	);
	const confirmDeleteModal = useConfirmModal(
		() => ({
			title: `Delete selected ${type}s?`,
			body: `Are you sure you want to delete these ${type}s? Once you remove ${
				type === "image" ? "an image" : "a video"
			} it will no longer appear in your media library.`,
			confirmColor: "pink",
			onConfirm: handleDelete,
		}),
		[handleDelete, type]
	);

	const handleSetDefaultImage = useCallback(
		close => {
			const selected = selectedMedia[0];

			put("update default image", `/images/make-default`, {image_id: [selected.id]}, () => {
				setMedia(oldMedia =>
					oldMedia
						.map(iv => ({
							...iv,
							isDefault: iv.id === selected.id,
						}))
						.sort(a => (a.isDefault ? -1 : 1))
				);
				onChangeSelectedMedia([]);
			});
			close();
		},
		[onChangeSelectedMedia, put, selectedMedia]
	);
	const confirmMakeDefaultImageModal = useConfirmModal(
		() => ({
			title: "Set image as default?",
			body:
				"Do you want to set this image as default? Once you set an image as default, it will be used by default for all the upcoming new posts.",
			onConfirm: handleSetDefaultImage,
		}),
		[handleSetDefaultImage]
	);

	const handleScroll = useCallback(() => {
		const container = containerRef.current;

		if (!container) return;

		if (container.scrollTop + container.clientHeight >= container.scrollHeight && !loading) {
			fetchData(filter);
		}
	}, [fetchData, filter, loading]);

	useEffect(() => {
		const container = containerRef.current;

		if (!container) return;

		container.addEventListener("scroll", handleScroll);
		return () => {
			container.removeEventListener("scroll", handleScroll);
		};
	}, [handleScroll]);

	const scrollToTop = useCallback(() => {
		const list = containerRef.current;

		if (!list || selectedMedia.length === 0) return;

		const index = media.findIndex(m => m.id === selectedMedia[0].id) + 1 + (uploading ? 1 : 0);
		const item = containerRef.current.children[index];
		item.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, [media, selectedMedia, uploading]);

	useEffect(() => {
		const debouncedEffect = debounce(resetFetchData, 500);

		debouncedEffect();

		return () => debouncedEffect.cancel();
	}, [resetFetchData]);

	useEffect(() => {
		if (isDetailView) {
			scrollToTop();
		}
	}, [isDetailView, scrollToTop]);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!selectedMedia.length || selectedMedia.length > 1) {
			setIsDetailView(false);
		}
	}, [selectedMedia]);

	useEffect(() => {
		get("users", `${type}s/users`, data => setUsers(inflateMediaUsers(data ?? [])));
	}, [type, get]);

	const handleSelectMediaKeyDown = (e: KeyboardEvent<HTMLDivElement>, m: Media, selected: boolean) => {
		switch (e.key) {
			case "Enter":
			case " ":
				onChangeSelectedMedia(
					selected ? selectedMedia.filter(existing => existing.id !== m.id) : [...selectedMedia, m]
				);

				break;
			default:
				return;
		}
		e.stopPropagation();
		e.preventDefault();
	};
	const handleSelectMedia = (media: Media) => onChangeSelectedMedia([media]);

	const handleImageLoad = event => {
		const {naturalWidth, naturalHeight} = event.target;

		setImageSize({width: naturalWidth, height: naturalHeight});
	};
	const handleSelect = (media: Media) => {
		if (selectedMedia.find(({id}) => id === media.id)) {
			return onChangeSelectedMedia(selectedMedia.filter(({id}) => id !== media.id));
		}

		onChangeSelectedMedia([...selectedMedia, media]);
	};

	const handleFileChange = (currentFile?: File) => {
		if (!currentFile) {
			return;
		}

		setFile(currentFile);

		if (!currentFile || type !== "image") {
			return;
		}

		const formData = new FormData();
		formData.append(type, currentFile);
		formData.append("name", type);

		setUploading(true);

		post(
			`${type} list`,
			`/${type}s/${type}-list`,
			formData,
			data => {
				setUploading(false);
				setFile(undefined);

				imageUploadedModal.open();
				const newImage = inflateUploadedImage(data ?? {});
				const newMedia = {
					id: newImage.id,
					url: newImage.url,
					alt: newImage.title,
					title: newImage.title,
					isDefault: false,
					type,
				};
				setMedia(media => [newMedia, ...media]);
			},
			() => {
				setUploading(false);
				setFile(undefined);
			},
			{},
			false,
			true
		);
	};

	const handleImageUpdate = useCallback(
		(file: File) => {
			const formData = new FormData();
			formData.append(type, file);
			formData.append("name", type);
			formData.append("title", selectedMedia[0].title);

			setUploading(true);

			post(
				`${type}s list`,
				`/${type}s/${type}-list`,
				formData,
				() => {
					imageUploadedModal.open();
					resetFetchData();
					setUploading(false);
				},
				() => setUploading(false),
				{},
				false,
				true
			);
		},
		[resetFetchData, selectedMedia, post, type, imageUploadedModal]
	);

	const editImageModal = useModal(
		({close}) => ({
			size: "large",
			body: (
				<Suspense fallback={<Loading />}>
					<ImageEditor
						imageUrl={selectedMedia[0]?.url}
						onSave={file => {
							close();
							handleImageUpdate(file);
						}}
						onClose={close}
					/>
				</Suspense>
			),
		}),
		[selectedMedia, handleImageUpdate]
	);

	const handleUploadError = useCallback(
		(err: string) => {
			setFile(undefined);
			toast({color: "red", text: err, icon: "warning"});
		},
		[toast]
	);

	const handleUploadSuccess = useCallback(() => {
		setFile(undefined);
		resetFetchData();
	}, [resetFetchData]);

	const debouncedUpdateTitle = debounce((selected: Media, title: string) => {
		put(`update ${type} title`, `/${type}s/${selected.id}`, {title}, () => undefined);
	}, 300);

	const handleChangeTitle = (title: string) => {
		const selected = selectedMedia[0];

		setMedia(media => media.map(m => ({...m, title: m.id === selected.id ? title : m.title})));
		debouncedUpdateTitle(selected, title);
	};

	const determineGridRowNumber = (): number => {
		if (windowWidth >= 1024) {
			return 4;
		}
		if (windowWidth >= 768) {
			return 3;
		}

		return 2;
	};

	const Shimmers = (fill: number) =>
		Array(fill)
			.fill(null)
			.map((m, i) => <Breathing className={styles.mediaItem} key={i} />);

	return (
		<div className={classnames(styles.container, className)}>
			<>
				<div className={styles.headerButtons}>
					{selectedMedia.length > 0 ? (
						<div className={styles.selectButtonContainer}>
							<h5 className={styles.selectedItemsText}>{count(selectedMedia, "item")} selected</h5>
							<div className={styles.selectItemsContainer}>
								<InputRow position="left">
									<Button
										value={`Deselect${selectedMedia.length > 1 ? " All" : ""}`}
										invert
										onClick={() => {
											setIsDetailView(false);
											onChangeSelectedMedia([]);
										}}
									/>
									<Button value="Delete" icon="delete" invert onClick={confirmDeleteModal.open} />
								</InputRow>
							</div>

							<div className={styles.selectItemsContainer}>
								<InputRow position="right">
									<Button
										value="Detail View"
										disabled={selectedMedia.length !== 1}
										invert
										onClick={() => setIsDetailView(c => !c)}
									/>
									{type === "image" && (
										<Button
											value="Edit content"
											disabled={selectedMedia.length !== 1}
											invert
											onClick={editImageModal.open}
										/>
									)}
								</InputRow>
							</div>
						</div>
					) : (
						<InputRow position="left" className={styles.filterButtons}>
							<Text icon="search" value={search} onChange={setSearch} placeholder="Search" />
							<Select
								placeholder="Created by"
								value={createdBy}
								onChange={setCreatedBy}
								options={[
									{
										label: "All Users",
										value: 0,
									},
									...users,
								]}
							/>
						</InputRow>
					)}
				</div>
				<div className={styles.mediaContainer} ref={containerRef}>
					<div className={classnames(styles.mediaItem, styles.uploadItem)}>
						{file && type === "video" ? (
							<S3UploadComponent file={file} onSuccess={handleUploadSuccess} onError={handleUploadError} />
						) : (
							<FileUpload
								iconSize={80}
								onChange={handleFileChange}
								type={type}
								value={file}
								className={styles.upload}
							/>
						)}
					</div>
					{uploading && Shimmers(1)}
					{media.map((m, i) => {
						const selected = selectedMedia.find(c => m.id === c.id);

						return (
							<>
								<div
									className={classnames(styles.mediaItem, styles.previewCard, selected && styles.selected)}
									key={`${m.id}-${m.type}-${m.url}`}
									tabIndex={0}
									onKeyDown={e => handleSelectMediaKeyDown(e, m, !!selected)}
									onClick={() => handleSelectMedia(m)}
								>
									<Checkbox
										className={styles.mediaItemCheckbox}
										value={!!selected}
										onChange={() => handleSelect(m)}
									/>
									{type === "image" && <ImageLoader media={m} className={styles.image} />}
									{type === "video" && (
										<video className={styles.video} src={m.url} controls muted>
											<source src={m.url} />
											<track kind="captions" srcLang="en" label="english_captions" />
										</video>
									)}
									{type === "image" && (
										<Span trim={1} color="grey" className={styles.previewTitle}>
											{m.title}
										</Span>
									)}
								</div>
								{selected && isDetailView && (
									<div
										tabIndex={0}
										style={{gridRow: `${Math.ceil((i + 2) / determineGridRowNumber()) + 1}`}}
										className={classnames(styles.mediaItem, styles.detailedCard)}
									>
										<Card className={styles.card}>
											<div className={styles.header}>
												<h3 className={styles.headerTitle}>Detail View</h3>
												<Icon icon="close" onClick={() => setIsDetailView(false)} />
											</div>

											<div className={styles.body}>
												<div className={classnames(styles.placeholder, styles.imageContainer)}>
													{type === "image" && (
														<img alt={m.alt} className={styles.image} src={m.url} onLoad={handleImageLoad} />
													)}
													{type === "video" && (
														<video className={styles.video} src={m.url} controls muted>
															<source src={m.url} />
															<track kind="captions" srcLang="en" label="english_captions" />
														</video>
													)}
												</div>
												<div className={classnames(styles.placeholder, styles.inputContainer)}>
													{type === "image" ? (
														<>
															<MultiColumn className={[styles.inputs, styles.info]}>
																<Text
																	text={`${imageSize.width} x ${imageSize.height}px`}
																	placeholder="Image name"
																	value={m.title}
																	onChange={handleChangeTitle}
																	validate={required}
																	maxLength={100}
																/>
																<Checkbox
																	disabled={m.isDefault}
																	value={m.isDefault}
																	onChange={confirmMakeDefaultImageModal.open}
																	label="Set as Default"
																/>
															</MultiColumn>
															<InputRow position="between">
																<Button
																	value="Edit Content"
																	icon="magic"
																	invert
																	onClick={editImageModal.open}
																/>
																<Button value="Select Image" icon="check" onClick={onConfirm} />
															</InputRow>
														</>
													) : (
														<InputRow position="right">
															<Button value="Select Video" icon="check" onClick={onConfirm} />
														</InputRow>
													)}
												</div>
											</div>
										</Card>
									</div>
								)}
							</>
						);
					})}
					{loading && Shimmers(8)}
				</div>
			</>
		</div>
	);
};
